import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {DocumentReadWReferences, DocumentType, FileType, WatchPhrase} from '../../../schemas';
import TextView from "../DocTypeViews/DocTypeDisplayText";
import {stripTranscriptJunk} from '../DocTypeViews/DocTypeDisplayText/shared/text-matching';
import Button from "../../../components/SharedComponents/Button/Button";
import PDFView from '../DocTypeViews/DocTypeDisplayPDF';
import {WatchPhraseHighlightColors} from "../../../colors";
import {CreateSearchFloatPopup, PERSISTENT_WATCH_PHRASE_HIGHLIGHTING, TEMPORARY_WATCH_PHRASE_HIGHLIGHTING} from "../../../feature_flags";
export interface HighlightOption {
    label: string;
    color: string;
    id?: number;
}
import {SlArrowDown, SlArrowLeft, SlArrowRight} from "react-icons/sl";
import MetadataPalette from "../DocTypeViews/MetadataPalette";
import TranscriptAnalysisView from "../DocTypeViews/DocTypeDisplayText/TranscriptAnalysisView";
import SpotlightBar from "../DocTypeViews/SpotlightBar";
import DocumentToolsSearch from "../../../components/SharedComponents/DocumentToolsSearch";
import DocumentToolsTagging from "../../../components/SharedComponents/DocumentToolsTagging";
import {useEnhancedDocumentsContext} from "../../../contexts/doc_contexts";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {useDocumentSpotlight} from "../../../contexts/document_spotlight_context";


export enum DocDisplayType {
    PDF = "PDF",
    TXT = "TXT",
    SPLIT = "SPLIT",
}

/** Handle exposed by PDFView for programmatic search navigation */
export interface PdfSearchHandle {
    highlight: (text: string, targetPage?: number) => Promise<number>;
    jumpToMatch: (index: number) => void;
    jumpToNextMatch: () => void;
    jumpToPreviousMatch: () => void;
    clearHighlights: () => void;
    jumpToPage: (pageIndex: number) => void;
}


/**
 * Extract a single transcript line for PDF search.
 *
 * The PDF text layer may have line numbers baked inline (e.g., "about11 disposing")
 * depending on the PDF's text rendering order. Any search text that crosses a
 * physical line boundary will hit an inline line number the seam can't bridge.
 *
 * Fix: split the quote on newlines, strip gutter numbers, and pick the longest
 * single line. One body line = one physical PDF line = no line-number junction.
 */
function bestSingleLine(quote: string): string {
    const lines = quote.split('\n')
        .map(ln => ln.replace(/^\s*\d{1,3}\s{1,6}/, '').trim())
        .filter(ln => ln.length >= 8);
    if (lines.length === 0) return stripTranscriptJunk(quote).slice(0, 40);
    // Use the first substantial line — stays near the start of the quote
    return lines[0];
}

function singlePageContent(quote: string): string {
    return bestSingleLine(quote);
}

// stripTranscriptJunk replaced by shared stripTranscriptJunk from text-matching.ts

/** Grab surrounding context from body at a known character position. */
function expandFromPosition(body: string, charIdx: number, matchLen: number): string {
    const contextChars = 80;
    let start = Math.max(0, charIdx - contextChars);
    const end = Math.min(body.length, charIdx + matchLen + contextChars);
    // Snap start forward to a word/line boundary
    if (start > 0) {
        const nl = body.indexOf('\n', start);
        if (nl !== -1 && nl < charIdx) start = nl + 1;
    }
    const expanded = body.slice(start, end).trim();
    return expanded.length > 350 ? expanded.slice(0, 350) : expanded;
}

const _fileTypeToDisplayType: Partial<Record<FileType, DocDisplayType>> = {
    [FileType.PDF]: DocDisplayType.PDF,
    [FileType.TXT]: DocDisplayType.TXT,
    [FileType.HTML]: DocDisplayType.TXT,
    [FileType.DOCX]: DocDisplayType.TXT,
    [FileType.JPEG]: DocDisplayType.TXT,
    [FileType.PNG]: DocDisplayType.TXT,
};

interface TabItem {
    label: string;
    type: DocDisplayType;
    disabled?: boolean;
}

// ── Reusable collapsible palette wrapper ─────────────────────────────────

const CollapsiblePalette: React.FC<{
    title: string;
    defaultOpen?: boolean;
    /** If provided, the palette's open state is controlled externally. */
    isOpen?: boolean;
    onToggle?: () => void;
    children: React.ReactNode;
}> = ({title, defaultOpen = true, isOpen: controlledOpen, onToggle, children}) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;
    const toggle = () => {
        if (isControlled) onToggle?.();
        else setInternalOpen(o => !o);
    };
    return (
        <div className="rounded-lg border border-slate-300 bg-white shadow-sm w-full">
            <button
                type="button"
                onClick={toggle}
                className="flex w-full items-center justify-between px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
                <span className="flex items-center gap-2">
                    {isOpen
                        ? <SlArrowDown className="m-1 w-2.5 h-2.5 rounded text-slate-600 hover:text-slate-900"/>
                        : <SlArrowRight className="m-1 w-2.5 h-2.5 rounded text-slate-600 hover:text-slate-900"/>
                    }
                    <span>{title}</span>
                </span>
            </button>
            <div className={`transition-all duration-300 ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div className="px-3 py-3">
                    {children}
                </div>
            </div>
        </div>
    );
};

// ── Palettes-only component (for expanded mode right panel) ──────────────

export const DocumentPalettes: React.FC<{
    doc: DocumentReadWReferences;
    setActiveWatchPhrasesCallback?: (phrases: WatchPhrase[]) => void;
}> = ({doc, setActiveWatchPhrasesCallback}) => {
    const isTranscript = doc.type === DocumentType.TRANSCRIPT;
    // Both analysis and codes can be open independently.
    // On transcripts, default both open; on other types, just codes.
    const [activeHeavy, setActiveHeavy] = useState<Set<string>>(
        isTranscript ? new Set(['analysis', 'codes']) : new Set(['codes'])
    );

    return (
        <div className="space-y-2 p-3 pb-8">
            {/* Metadata */}
            <MetadataPalette doc={doc}/>

            {/* Transcript Analysis */}
            {isTranscript && (
                <TranscriptAnalysisView
                    doc={doc as DocumentReadWReferences}
                    isOpen={activeHeavy.has('analysis')}
                    onToggle={() => setActiveHeavy(cur => { const next = new Set(cur); next.has('analysis') ? next.delete('analysis') : next.add('analysis'); return next; })}
                />
            )}

            {/* Codes */}
            <CollapsiblePalette
                title="Codes"
                isOpen={activeHeavy.has('codes')}
                onToggle={() => setActiveHeavy(cur => { const next = new Set(cur); next.has('codes') ? next.delete('codes') : next.add('codes'); return next; })}
            >
                <DocumentToolsTagging doc={doc}/>
            </CollapsiblePalette>

            {/* Watch List */}
            {setActiveWatchPhrasesCallback && (PERSISTENT_WATCH_PHRASE_HIGHLIGHTING || TEMPORARY_WATCH_PHRASE_HIGHLIGHTING) && (
                <CollapsiblePalette title="Watch List" defaultOpen={false}>
                    <DocumentToolsSearch
                        setActiveWatchPhrasesCallback={setActiveWatchPhrasesCallback}
                    />
                </CollapsiblePalette>
            )}
        </div>
    );
};

// ── Main component ──────────────────────────────────────────────────────

interface DocumentDetailsViewportProps {
    highlightOptions: HighlightOption[];
    setHighlightOptions: React.Dispatch<React.SetStateAction<HighlightOption[]>>;
    setActiveWatchPhrasesCallback?: (phrases: WatchPhrase[]) => void;
    showBackButton?: boolean;
    showPalettes?: boolean;
    previewMode?: boolean;
    children?: ReactNode;
}

const DocumentPageView: React.FC<DocumentDetailsViewportProps> = ({
    highlightOptions,
    setHighlightOptions,
    setActiveWatchPhrasesCallback,
    showBackButton = true,
    showPalettes = true,
    children
}) => {
    const {goBack} = useNavigationContext()!;
    const {
        document,
        setDocument,
    } = useEnhancedDocumentsContext()!;
    const spotlightCtx = useDocumentSpotlight();
    const pdfSearchRef = useRef<PdfSearchHandle | null>(null);
    const [pdfReady, setPdfReady] = useState(false);
    const splitTextRef = useRef<HTMLDivElement | null>(null);
    /** True while a spotlight jump is programmatically scrolling the PDF.
     *  Prevents the PDF→text scroll sync from overwriting the text view's
     *  independently-calculated spotlight position. */
    const isSpotlightScrolling = useRef(false);
    const actualPdfPageRef = useRef(0);
    const splitPdfWrapperRef = useRef<HTMLDivElement | null>(null);
    const pdfPageCountRef = useRef(0);
    const splitSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [loading] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({x: 0, y: 0});
    const [selectedText, setSelectedText] = useState('');
    const [selectedView, setSelectedView] = useState<DocDisplayType>(DocDisplayType.TXT);
    const userPickedTab = useRef(false);
    const [tabButtons, setTabButtons] = useState<TabItem[]>([]);
    // Both analysis and codes can be open independently.
    // Default: transcripts get both, others get codes only. Resets on doc change.
    const [activeHeavy, setActiveHeavy] = useState<Set<string>>(() =>
        document?.type === DocumentType.TRANSCRIPT
            ? new Set(['analysis', 'codes'])
            : new Set(['codes'])
    );
    useEffect(() => {
        if (!document) return;
        setActiveHeavy(document.type === DocumentType.TRANSCRIPT
            ? new Set(['analysis', 'codes'])
            : new Set(['codes']));
    }, [document?.id]);

    useEffect(() => {
        if (!document) return;
        const isPdf = document.file_type === FileType.PDF;
        const hasOriginal = isPdf || !!document.body;
        const tabs: TabItem[] = [];
        tabs.push({label: 'Extracted Content', type: DocDisplayType.TXT});
        tabs.push({
            label: 'Original File',
            type: DocDisplayType.PDF,
            disabled: !hasOriginal,
        });
        tabs.push({
            label: 'Side by Side',
            type: DocDisplayType.SPLIT,
            disabled: !isPdf,
        });
        setTabButtons(tabs);
        setSelectedView(prev => {
            if (userPickedTab.current) {
                if (prev === DocDisplayType.PDF && !hasOriginal) return DocDisplayType.TXT;
                if (prev === DocDisplayType.SPLIT && !isPdf) return DocDisplayType.TXT;
                return prev;
            }
            const isTranscript = document.type === DocumentType.TRANSCRIPT;
            const isExpanded = !showPalettes;
            if (isTranscript) {
                if (isExpanded && isPdf) return DocDisplayType.SPLIT;
                return DocDisplayType.TXT;
            }
            if (hasOriginal) return DocDisplayType.PDF;
            return DocDisplayType.TXT;
        });
    }, [document?.id]);

    // Clear spotlights and reset PDF ready state when document changes.
    useEffect(() => {
        spotlightCtx?.clearAllSpotlights();
        setPdfReady(false);
        userPickedTab.current = false;
    }, [document?.id]);

    // Clear PDF search highlights when all spotlights are removed
    useEffect(() => {
        if ((spotlightCtx?.spotlights ?? []).length === 0) {
            pdfSearchRef.current?.clearHighlights();
        }
    }, [spotlightCtx?.spotlights]);

    const viewHasPdf = (selectedView === DocDisplayType.PDF || selectedView === DocDisplayType.SPLIT) && document?.file_type === FileType.PDF;

    /** Tracks which jumpRequest.seq has been fully processed by Phase 2.
     *  Used by handleSplitPdfPageChange to block sync only while a jump
     *  is in-flight (between Phase 1 and Phase 2 completing). */
    const processedJumpSeqRef = useRef(0);

    /** Mark spotlight scrolling done after the PDF scroll animation settles.
     *  The PDF viewer may take 1-2s to load pages and fire its final
     *  onPageChange — 400ms was too short and let the PDF→text sync
     *  override the spotlight's text position. */
    const clearSpotlightScrolling = useCallback(() => {
        setTimeout(() => { isSpotlightScrolling.current = false; }, 1500);
    }, []);

    // React to jump requests — find the spotlight, activate it, trigger search.
    // Two phases:
    //   1. On jumpRequest: activate spotlight + scroll content viewer into viewport
    //      (this forces the react-pdf-viewer to enter the viewport and start loading)
    //   2. On pdfReady: actually run the search + jump to match
    const jumpRequest = spotlightCtx?.jumpRequest ?? null;
    const jumpRequestRef = useRef(jumpRequest);
    jumpRequestRef.current = jumpRequest;

    // Phase 1: activate + scroll content viewer into viewport
    useEffect(() => {
        if (!jumpRequest) return;
        const spotlight = (spotlightCtx?.spotlights ?? []).find(s => s.text === jumpRequest.text && s.visible);
        if (!spotlight) return;

        spotlightCtx?.setActiveSpotlightId(spotlight.id);

        // Scroll content viewer into viewport — instant (not smooth) to avoid
        // competing with useLineSpotlight's smooth scroll to the matched line
        const contentViewer = window.document.getElementById('document-content-viewer');
        contentViewer?.scrollIntoView({behavior: 'auto', block: 'nearest'});
    }, [jumpRequest]);

    // Page-only jump: just navigate to the page, no text search.
    // Used by examination block clicks in the metadata palette.
    const pageJumpRequest = spotlightCtx?.pageJumpRequest;
    useEffect(() => {
        if (!pageJumpRequest || !pdfSearchRef.current) return;
        isSpotlightScrolling.current = true;
        // jumpToPage is 0-indexed, pageJumpRequest.page is 1-indexed
        pdfSearchRef.current.jumpToPage(pageJumpRequest.page - 1);
        clearSpotlightScrolling();
    }, [pageJumpRequest, clearSpotlightScrolling]);

    // Phase 2: once PDF is ready, run the search for the pending jump.
    // Works in both PDF-only and SPLIT modes.
    // The PDF and text sides navigate INDEPENDENTLY — this only handles the
    // PDF side. The text side is handled by useLineSpotlight in the text viewer.
    // isSpotlightScrolling prevents the PDF→text scroll sync from interfering.

    useEffect(() => {
        if (!jumpRequest || !pdfReady || !pdfSearchRef.current || !viewHasPdf) return;

        const spotlight = (spotlightCtx?.spotlights ?? []).find(s => s.text === jumpRequest.text && s.visible);
        if (!spotlight) return;

        // Mark this jump as processed so handleSplitPdfPageChange unblocks
        processedJumpSeqRef.current = jumpRequest.seq;

        // Block PDF→text sync while the PDF programmatically scrolls
        isSpotlightScrolling.current = true;

        const ref = pdfSearchRef.current;

        // Page spotlight: just jump to the page, no text search
        if (spotlight.kind === 'page' && spotlight.pageHint != null) {
            ref.jumpToPage(spotlight.pageHint - 1);
            spotlightCtx?.updateMatchInfo(0, 0);
            clearSpotlightScrolling();
            return;
        }

        // Line spotlight: search PDF for the pre-expanded context text (single match)
        if (spotlight.kind === 'line' && spotlight.matchItems?.length) {
            ref.highlight(spotlight.matchItems[0].text).then(count => {
                spotlightCtx?.updateMatchInfo(0, count > 0 ? 1 : 0);
                clearSpotlightScrolling();
            });
            return;
        }

        // Speaker spotlight: search for the first match item&apos;s text (expanded with context)
        if (spotlight.kind === 'speaker' && spotlight.matchItems?.length) {
            const firstItem = spotlight.matchItems[0];
            const searchText = expandMatchText(firstItem.text);
            ref.highlight(searchText).then(_count => {
                if (selectedView === DocDisplayType.PDF) {
                    spotlightCtx?.updateMatchInfo(0, spotlight.matchItems!.length);
                }
                clearSpotlightScrolling();
            });
            return;
        }

        // Text spotlight: if the quote spans multiple pages in the body,
        // extract just the content from the page with the most text.
        // PDF search is per-page so cross-page quotes get 0 matches.
        const pdfSearchText = singlePageContent(spotlight.text);
        console.log('[Spotlight Phase2] text spotlight:', {
            rawText: spotlight.text?.slice(0, 80),
            pageHint: spotlight.pageHint,
            pdfSearchText: pdfSearchText?.slice(0, 120),
        });
        ref.highlight(pdfSearchText, spotlight.pageHint).then(count => {
            if (selectedView === DocDisplayType.PDF) {
                spotlightCtx?.updateMatchInfo(0, count);
            }
            if (count === 0 && spotlight.pageHint) {
                ref.jumpToPage(spotlight.pageHint - 1);
            }
            clearSpotlightScrolling();
        });
    }, [jumpRequest, pdfReady, viewHasPdf, clearSpotlightScrolling]);

    /** Helper: get active spotlight if it has pre-computed matchItems */
    const getActiveSpeakerSpotlight = useCallback(() => {
        if (!spotlightCtx) return null;
        const active = spotlightCtx.spotlights.find(s => s.id === spotlightCtx.activeSpotlightId);
        return (active?.kind === 'speaker' && active.matchItems?.length) ? active : null;
    }, [spotlightCtx]);

    /**
     * Expand a speaker match item's text with surrounding characters from the
     * document body so the PDF regex search finds a unique match instead of the
     * first occurrence of a common short phrase like "A Yes." or "Q Did you?".
     *
     * Finds the text in the body by content match, then grabs characters before
     * and after to build a unique search string.
     */
    const expandMatchText = useCallback((matchText: string): string => {
        const body = document?.body;
        if (!body || !matchText) return matchText;

        const idx = body.indexOf(matchText);
        if (idx === -1) {
            const lowerIdx = body.toLowerCase().indexOf(matchText.toLowerCase());
            if (lowerIdx === -1) return matchText;
            return expandFromPosition(body, lowerIdx, matchText.length);
        }
        return expandFromPosition(body, idx, matchText.length);
    }, [document?.body]);

    /** Navigate PDF to a specific speaker match item */
    const navigatePdfToMatchItem = useCallback((matchText: string) => {
        if (!pdfSearchRef.current) return;
        isSpotlightScrolling.current = true;
        const searchText = expandMatchText(matchText);
        pdfSearchRef.current.highlight(searchText).then(count => {
            if (count > 0) pdfSearchRef.current?.jumpToMatch(0);
            clearSpotlightScrolling();
        });
    }, [expandMatchText, clearSpotlightScrolling]);

    const handleSpotlightPrev = useCallback(() => {
        if (!spotlightCtx) return;

        const speakerSpotlight = getActiveSpeakerSpotlight();
        if (speakerSpotlight) {
            // Speaker spotlight: let text view handle scroll + match tracking
            if (spotlightCtx.textNavRef.current) {
                spotlightCtx.textNavRef.current.jumpToPreviousMatch();
            }
            // Navigate PDF to the previous match item
            if (viewHasPdf) {
                const {currentMatch} = spotlightCtx.activeMatchInfo;
                const total = speakerSpotlight.matchItems!.length;
                const prev = currentMatch > 0 ? currentMatch - 1 : total - 1;
                navigatePdfToMatchItem(speakerSpotlight.matchItems![prev].text);
                // In PDF-only mode (no text view), manually update match info
                if (!spotlightCtx.textNavRef.current) {
                    spotlightCtx.updateMatchInfo(prev, total);
                }
            }
            return;
        }

        // Text spotlight: existing flow
        const hasPdf = viewHasPdf && pdfSearchRef.current;
        if (hasPdf) {
            isSpotlightScrolling.current = true;
            pdfSearchRef.current!.jumpToPreviousMatch();
            clearSpotlightScrolling();
            const {currentMatch, totalMatches} = spotlightCtx.activeMatchInfo;
            const prev = currentMatch > 0 ? currentMatch - 1 : totalMatches - 1;
            spotlightCtx.updateMatchInfo(prev, totalMatches);
        } else if (spotlightCtx.textNavRef.current) {
            spotlightCtx.textNavRef.current.jumpToPreviousMatch();
        }
    }, [spotlightCtx, viewHasPdf, getActiveSpeakerSpotlight, navigatePdfToMatchItem, clearSpotlightScrolling]);

    const handleSpotlightNext = useCallback(() => {
        if (!spotlightCtx) return;

        const speakerSpotlight = getActiveSpeakerSpotlight();
        if (speakerSpotlight) {
            // Speaker spotlight: let text view handle scroll + match tracking
            if (spotlightCtx.textNavRef.current) {
                spotlightCtx.textNavRef.current.jumpToNextMatch();
            }
            // Navigate PDF to the next match item
            if (viewHasPdf) {
                const {currentMatch} = spotlightCtx.activeMatchInfo;
                const total = speakerSpotlight.matchItems!.length;
                const next = (currentMatch + 1) % total;
                navigatePdfToMatchItem(speakerSpotlight.matchItems![next].text);
                // In PDF-only mode (no text view), manually update match info
                if (!spotlightCtx.textNavRef.current) {
                    spotlightCtx.updateMatchInfo(next, total);
                }
            }
            return;
        }

        // Text spotlight: existing flow
        const hasPdf = viewHasPdf && pdfSearchRef.current;
        if (hasPdf) {
            isSpotlightScrolling.current = true;
            pdfSearchRef.current!.jumpToNextMatch();
            clearSpotlightScrolling();
            const {currentMatch, totalMatches} = spotlightCtx.activeMatchInfo;
            const next = currentMatch < totalMatches - 1 ? currentMatch + 1 : 0;
            spotlightCtx.updateMatchInfo(next, totalMatches);
        } else if (spotlightCtx.textNavRef.current) {
            spotlightCtx.textNavRef.current.jumpToNextMatch();
        }
    }, [spotlightCtx, viewHasPdf, getActiveSpeakerSpotlight, navigatePdfToMatchItem, clearSpotlightScrolling]);

    // ── Split-view scroll sync (PDF → text) ─────────────────────────────────
    // Uses onPageChange exclusively — when the PDF reports a new visible page,
    // find the corresponding element in the text view via data-page attribute
    // and scroll to it. No fraction estimation, no correction factors.
    // Skipped during spotlight jumps (text navigates independently).
    //
    // The text side's `data-page` is computed from `page_body_offsets`, which
    // are PDF page boundaries in the body (see charOffsetToPageNumber). So
    // PDF page N (1-based) lines up directly with `data-page="N"`. Don't apply
    // `transcript_to_pdf_page` here — that map exists to translate human-facing
    // *transcript* page numbers (which can differ from PDF pages when a
    // deposition has a cover/title page), and `data-page` doesn't speak that
    // language. Earlier versions tried to remap through it and ended up
    // jumping to the cover page on every transcript page after the cover.

    const handleSplitPdfPageChange = useCallback((pageIdx: number) => {
        // Skip during spotlight jumps — text and PDF navigate independently
        if (isSpotlightScrolling.current) return;
        // Skip when a spotlight jump is pending — Phase 2 hasn't fired yet
        // but the PDF is already emitting onPageChange during initial load.
        // Once Phase 2 marks the seq as processed, this guard lifts.
        const jr = jumpRequestRef.current;
        if (jr && jr.seq !== processedJumpSeqRef.current) return;

        actualPdfPageRef.current = pageIdx;

        // Debounce: wait for the page to stabilize before syncing.
        // During fast scrolling, onPageChange fires rapidly — we only want to
        // sync once the user pauses, then animate smoothly to that position.
        if (splitSyncTimerRef.current) clearTimeout(splitSyncTimerRef.current);
        splitSyncTimerRef.current = setTimeout(() => {
            const textEl = splitTextRef.current;
            if (!textEl) return;

            // PDF page N (1-based) ↔ data-page="N" on the text side.
            const textPage = pageIdx + 1;
            const el = textEl.querySelector(`[data-page="${textPage}"]`);
            if (el) {
                (el as HTMLElement).scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        }, 150);
    }, []);

    // ── Split-view click-to-sync ─────────────────────────────────────────
    // Clicking a line in the text view creates a temporary "sync" spotlight.
    // The spotlight system then searches the PDF for that text and jumps to it.

    /** Handle click on text side in split view — create a line-based sync spotlight.
     *
     *  Text view: highlights only the clicked line (by index, no text search).
     *  PDF view:  searches for expanded surrounding context from the exact body
     *             position (via data-body-offset) to find the right PDF location.
     */
    const handleSplitTextClick = useCallback((e: React.MouseEvent) => {
        if (!spotlightCtx) return;
        const lineEl = (e.target as HTMLElement).closest('[data-line-index]') as HTMLElement | null;
        if (!lineEl) return;

        const lineText = (lineEl.dataset.content ?? '').trim();
        if (!lineText || lineText.length < 5) return;

        const lineIdx = parseInt(lineEl.dataset.lineIndex ?? '-1', 10);
        if (lineIdx < 0) return;

        // Use the exact body char offset stored on the DOM element by TranscriptView.
        // This is the precise position — no searching, no ambiguity.
        const body = document?.body ?? '';
        const bodyOffset = parseInt(lineEl.dataset.bodyOffset ?? '-1', 10);
        let pdfSearchText = lineText;
        if (body && bodyOffset >= 0) {
            pdfSearchText = expandFromPosition(body, bodyOffset, lineText.length);
        }

        spotlightCtx.setSyncSpotlight({
            text: lineText,
            source: 'Sync',
            kind: 'line',
            matchItems: [{text: pdfSearchText, lineIndex: lineIdx}],
        });
    }, [spotlightCtx, document?.body]);

    // Clear sync spotlights and pending sync timer when leaving split view
    useEffect(() => {
        if (selectedView !== DocDisplayType.SPLIT) {
            spotlightCtx?.clearSyncSpotlights();
            if (splitSyncTimerRef.current) {
                clearTimeout(splitSyncTimerRef.current);
                splitSyncTimerRef.current = null;
            }
        }
    }, [selectedView]);

    if (!document) return <div>No document selected.</div>;

    const isTranscript = document.type === DocumentType.TRANSCRIPT;

    const handleTextSelection = () => {
        const text = window.getSelection()?.toString();
        if (text) {
            setSelectedText(text);
            const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
            if (rect) {
                setContextMenuPosition({x: rect.left + window.scrollX, y: rect.top + window.scrollY});
                setShowContextMenu(true);
            }
        } else {
            setShowContextMenu(false);
        }
    };

    const handleConfirmHighlight = () => {
        if (selectedText) {
            setHighlightOptions(prev => [
                ...prev,
                {label: selectedText, color: WatchPhraseHighlightColors.fromId()}
            ]);
        }
        setShowContextMenu(false);
    };

    return (
        <>
            <div
                className="bg-white rounded-lg flex flex-col flex-1 min-h-0 border border-slate-200 shadow-sm"
                onMouseUp={handleTextSelection}
            >
                {/* ── Upper section: palettes + tabs (scrollable, capped) ──── */}
                <div className="shrink-0 overflow-auto max-h-[45vh]">
                    {/* Header bar — only show back button when needed (nav lives in DocumentToolsPanel) */}
                    {showBackButton && (
                        <div className="flex w-full items-center border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500">
                            <button
                                onClick={() => {
                                    setDocument(null);
                                    goBack();
                                }}
                                title="Go back"
                                className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 hover:bg-slate-50 transition-colors"
                            >
                                <SlArrowLeft className="w-3 h-3"/>
                                <span>Back</span>
                            </button>
                        </div>
                    )}

                    {/* Optional child content */}
                    {children && (
                        <div className="border-b border-slate-100 bg-slate-50/50 px-3 py-2">
                            {children}
                        </div>
                    )}

                    {/* Palettes — only shown when not split into a separate panel */}
                    {showPalettes && (
                        <>
                            {/* Metadata */}
                            <div className="px-3 py-2">
                                <MetadataPalette doc={document}/>
                            </div>

                            {/* Transcript Analysis palette (collapsible, like metadata) */}
                            {isTranscript && (
                                <div className="px-3 pb-2">
                                    <TranscriptAnalysisView
                                        doc={document as DocumentReadWReferences}
                                        isOpen={activeHeavy.has('analysis')}
                                        onToggle={() => setActiveHeavy(cur => { const next = new Set(cur); next.has('analysis') ? next.delete('analysis') : next.add('analysis'); return next; })}
                                    />
                                </div>
                            )}

                            {/* Codes palette */}
                            <div className="px-3 pb-2">
                                <CollapsiblePalette
                                    title="Codes"
                                    isOpen={activeHeavy.has('codes')}
                                    onToggle={() => setActiveHeavy(cur => { const next = new Set(cur); next.has('codes') ? next.delete('codes') : next.add('codes'); return next; })}
                                >
                                    <DocumentToolsTagging doc={document}/>
                                </CollapsiblePalette>
                            </div>

                            {/* Watch List palette — hidden when both watch list flags are off */}
                            {setActiveWatchPhrasesCallback && (PERSISTENT_WATCH_PHRASE_HIGHLIGHTING || TEMPORARY_WATCH_PHRASE_HIGHLIGHTING) && (
                                <div className="px-3 pb-2">
                                    <CollapsiblePalette title="Watch List" defaultOpen={false}>
                                        <DocumentToolsSearch
                                            setActiveWatchPhrasesCallback={setActiveWatchPhrasesCallback}
                                        />
                                    </CollapsiblePalette>
                                </div>
                            )}
                        </>
                    )}

                    {/* Tabs */}
                    <div className="flex border-b border-slate-200 text-xs font-medium px-1 sticky bottom-0 bg-white">
                        {tabButtons.map(tab => (
                            <button
                                key={tab.type}
                                title={tab.disabled ? "This document has no source file" : `View ${tab.label}`}
                                className={`px-3 py-2 border-b-2 transition-colors ${
                                    tab.disabled
                                        ? "opacity-40 cursor-not-allowed border-transparent"
                                        : selectedView === tab.type
                                            ? "border-indigo-500 text-indigo-700"
                                            : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                                }`}
                                onClick={() => { if (!tab.disabled) { userPickedTab.current = true; setSelectedView(tab.type); } }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Context menu */}
                {showContextMenu && CreateSearchFloatPopup && (
                    <Button
                        loading={loading}
                        title="Create Entity"
                        onClick={handleConfirmHighlight}
                        size="small"
                        style={{
                            position: "absolute",
                            top: `${contextMenuPosition.y}px`,
                            left: `${contextMenuPosition.x}px`,
                            zIndex: 1000,
                        }}
                    />
                )}

                {/* ── Spotlight bar — pinned between palettes and content ── */}
                <div className="shrink-0">
                    <SpotlightBar onPrev={handleSpotlightPrev} onNext={handleSpotlightNext}/>
                </div>

                {/* ── Content viewer — fills remaining height, scrolls independently ── */}
                {document && selectedView === DocDisplayType.SPLIT && document.file_type === FileType.PDF ? (
                    <div className="flex flex-1 min-h-0" id="document-content-viewer">
                        {/* Text side — scroll-locked with PDF */}
                        <div
                            ref={splitTextRef}
                            className="flex-1 overflow-auto p-2 border-r border-slate-200"
                            style={{minWidth: 0}}
                            onClick={handleSplitTextClick}
                        >
                            <TextView doc={document} highlightOptions={highlightOptions}/>
                        </div>
                        {/* PDF viewer — scroll-locked with text */}
                        <div ref={splitPdfWrapperRef} className="flex-1 min-h-0 flex flex-col" style={{minWidth: 0}}>
                            <PDFView
                                doc={document}
                                highlightOptions={highlightOptions}
                                searchRef={pdfSearchRef}
                                onReady={(n) => { setPdfReady(true); pdfPageCountRef.current = n; }}
                                onPageChange={handleSplitPdfPageChange}
                            />
                        </div>
                    </div>
                ) : document && selectedView === DocDisplayType.PDF && document.file_type === FileType.PDF ? (
                    /* PDF-only mode: no outer scroll wrapper — PDF viewer handles its own scroll */
                    <div className="flex-1 min-h-0 flex flex-col" id="document-content-viewer">
                        <PDFView
                            doc={document}
                            highlightOptions={highlightOptions}
                            searchRef={pdfSearchRef}
                            onReady={() => setPdfReady(true)}
                        />
                    </div>
                ) : document && selectedView === DocDisplayType.PDF && document.file_type !== FileType.PDF ? (
                    /* Raw text view for non-PDF "Original File" — unprocessed body for auditability */
                    <div className="flex-1 min-h-0 overflow-auto p-4 bg-slate-50" id="document-content-viewer">
                        <pre className="whitespace-pre-wrap font-mono text-xs text-slate-800 leading-relaxed">{document.body}</pre>
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 overflow-auto p-2" id="document-content-viewer">
                        {document && <TextView doc={document} highlightOptions={highlightOptions}/>}
                    </div>
                )}
            </div>
        </>
    );
};

export default DocumentPageView;
